import { mande, type MandeError } from 'mande'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface IncidentUpdate {
  body: string
  created_at: string
  status: string
  affected_components?: string[]
}

interface Incident {
  id: string
  name: string
  status: string
  created_at: string
  impact: string
  incident_updates: IncidentUpdate[]
}

interface Component {
  id: string
  name: string
  status: string
  group_id: string
  group: boolean
}

interface ComponentGroup {
  name: string
  components: Component[]
}

interface StatusData {
  active_incidents: Incident[]
  historical_incidents: Incident[]
  components: {
    groups: ComponentGroup[]
    standalone_components: Component[]
  }
}

export function useStatus() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/statuspage`)
  const language = useI18n().locale.value.split('-')[0]
  const statusData = ref<StatusData | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchStatus = async () => {
    try {
      const response = await api.get<{ payload: StatusData }>('', {
        headers: {
          'Accept-Language': language,
        },
      })
      statusData.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  fetchStatus().then(() => {})

  return {
    statusData,
    loading,
    error,
  }
}

export function useIncidentById(id: string = '') {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/statuspage/incident/`)
  const language = useI18n().locale.value.split('-')[0]
  const incident = ref<Incident | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchIncidentById = async () => {
    try {
      const response = await api.get<{ payload: Incident }>(id, {
        headers: {
          'Accept-Language': language,
        },
      })
      incident.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  fetchIncidentById().then(() => {})

  return {
    incident,
    loading,
    error,
  }
}

export function useSubscribeToStatus() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/statuspage/subscribe`, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

  const loading = ref(false)
  const error = ref<MandeError | null>(null)
  const success = ref(false)

  const subscribe = async (email: string) => {
    loading.value = true
    error.value = null
    success.value = false

    try {
      await api.post<{ payload: any }>({ email })
      success.value = true
    } catch (err) {
      error.value = err as MandeError
    } finally {
      loading.value = false
    }
  }

  return {
    subscribe,
    loading,
    error,
    success,
  }
}
